import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery } from '@apollo/client';
import UserContext from '../../context/UserProvider/UserContext';
import getMyMcpKeysQuery from '../../Apollo/queries/getMyMcpKeys';
import McpKeyDialog, { McpKeyDialogMode } from './McpKeyDialog';
import { formatDate, getScopeDisplayName } from './profileMcpUtils';

interface McpKey {
  id: string;
  key_prefix: string;
  label: string;
  scopes: string[];
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  createdAt: string;
}

function ProfilePage() {
  const { username, email, roles } = useContext(UserContext);
  const roleList = roles
    ? roles
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean)
    : [];

  const mcpUrl = `${window.location.origin}/mcp`;
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const handleCopyMcpUrl = () => {
    // CONTRACT: navigator.clipboard absent in non-secure/sandboxed contexts → silent no-op; manual copy still possible.
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(mcpUrl)
      .then(() => {
        if (!isMountedRef.current) return;
        setCopied(true);
        if (timerRef.current !== null) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<McpKeyDialogMode>({ type: 'CREATE' });
  // CONTRACT: revokeRefetchError — surfaced when post-revoke refetch fails (dialog already closed/unmounted).
  // McpKeyDialog.onKeyRevoked must NOT re-throw — errors are owned by ProfilePage, not the dialog.
  const [revokeRefetchError, setRevokeRefetchError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(getMyMcpKeysQuery, {
    fetchPolicy: 'network-only',
  });

  const keys: McpKey[] = data?.getMyMcpKeys ?? [];
  const activeKeys = keys.filter((k) => !k.revoked_at);

  const handleOpenCreate = () => {
    setDialogMode({ type: 'CREATE' });
    setDialogOpen(true);
  };

  const handleOpenRevoke = (key: McpKey) => {
    setDialogMode({ type: 'REVOKE_CONFIRM', id: key.id, label: key.label });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleKeyCreated = () => {
    refetch();
  };

  // CONTRACT: awaited by McpKeyDialog (onKeyRevoked: () => Promise<void>) — no fire-and-forget.
  // Errors caught here so McpKeyDialog's catch block is never triggered by refetch failures
  // (dialog is already unmounted after handleClose(); setState on unmounted = no-op in React).
  const handleKeyRevoked = async (): Promise<void> => {
    setRevokeRefetchError(null);
    try {
      await refetch();
    } catch {
      setRevokeRefetchError('Schlüsseldaten konnten nicht aktualisiert werden — bitte Seite neu laden.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mein Profil
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Benutzerinformationen
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Benutzername
          </Typography>
          <Typography variant="body2" data-testid="profile-username">
            {username || '—'}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            E-Mail
          </Typography>
          <Typography variant="body2" data-testid="profile-email">
            {email || '—'}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Rollen
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} data-testid="profile-roles">
            {roleList.length > 0 ? (
              roleList.map((role) => <Chip key={role} label={role} size="small" variant="outlined" />)
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Meine MCP-Schlüssel</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            size="small"
            data-testid="new-mcp-key-button"
          >
            Neuer Schlüssel
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Alert severity="info" icon={false} sx={{ mb: 2 }} data-testid="mcp-connection-hint">
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            So verbindest du deinen KI-Assistenten (z. B. Langdock)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              MCP-Adresse:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                bgcolor: 'background.paper',
                gap: 1,
                flexGrow: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', flexGrow: 1, wordBreak: 'break-all' }}
                data-testid="mcp-endpoint-url"
              >
                {mcpUrl}
              </Typography>
              <Tooltip title={copied ? 'Kopiert!' : 'MCP-Adresse kopieren'}>
                <IconButton
                  size="small"
                  onClick={handleCopyMcpUrl}
                  aria-label="MCP-Adresse kopieren"
                  data-testid="copy-mcp-url-button"
                  data-copied={copied ? 'true' : 'false'}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <Box component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body2">
                In den Connector-/MCP-Einstellungen deines KI-Dienstes einen Remote-MCP-Server mit dieser Adresse
                hinzufügen.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body2">
                Authentifizierung: Header{' '}
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  Authorization: Bearer &lt;dein Schlüssel&gt;
                </Box>{' '}
                (Schlüssel unten erstellen — wird nur <strong>EINMAL</strong> angezeigt).
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2">
                Danach den Assistenten anweisen, z.&nbsp;B.:{' '}
                <em>„Nutze den PRM-MCP, um meine Belege zu suchen/anzulegen.&#8220;</em> Die Tools erscheinen nach dem
                Verbinden automatisch.
              </Typography>
            </Box>
          </Box>
        </Alert>

        {revokeRefetchError && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="revoke-refetch-error">
            {revokeRefetchError}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">MCP-Schlüssel konnten nicht geladen werden: {error.message}</Alert>}

        {!loading && !error && activeKeys.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Noch keine MCP-Schlüssel vorhanden. Erstelle deinen ersten Schlüssel mit dem Button oben.
          </Typography>
        )}

        {!loading && !error && activeKeys.length > 0 && (
          <Table size="small" data-testid="mcp-keys-table">
            <TableHead>
              <TableRow>
                <TableCell>Bezeichnung</TableCell>
                <TableCell>Schlüssel-Präfix</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Ablaufdatum</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {activeKeys.map((key) => (
                <TableRow key={key.id} data-testid={`mcp-key-row-${key.id}`}>
                  <TableCell>{key.label}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {key.key_prefix}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {key.scopes.map((scope) => (
                        <Chip key={scope} label={getScopeDisplayName(scope)} size="small" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(key.expires_at)}</TableCell>
                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Schlüssel widerrufen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenRevoke(key)}
                        data-testid={`revoke-key-button-${key.id}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {dialogOpen && (
        <McpKeyDialog
          open={dialogOpen}
          mode={dialogMode}
          onClose={handleDialogClose}
          onKeyCreated={handleKeyCreated}
          onKeyRevoked={handleKeyRevoked}
        />
      )}
    </Container>
  );
}

export default ProfilePage;
